// src/pages/PolicyPage.js
import React, { useState } from 'react';
import { Truck, RefreshCw, Shield, Clock, MapPin, Phone, Mail, ChevronDown, ChevronUp } from 'lucide-react';

const Section = ({ icon: Icon, title, color, children }) => (
  <div className={`bg-white rounded-3xl p-6 md:p-8 shadow-soft border-l-4 ${color}`}>
    <div className="flex items-center gap-3 mb-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color.replace('border-', 'bg-').replace('-500', '-100')}`}>
        <Icon size={20} className={color.replace('border-', 'text-')} />
      </div>
      <h2 className="font-display font-bold text-xl text-gray-900">{title}</h2>
    </div>
    {children}
  </div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-rose-100 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-rose-50/50 transition-colors"
      >
        <span className="font-body font-medium text-gray-800 text-sm">{q}</span>
        {open ? <ChevronUp size={18} className="text-rose-400 shrink-0" /> : <ChevronDown size={18} className="text-rose-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-gray-500 font-body text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

export default function PolicyPage() {
  const [activeTab, setActiveTab] = useState('shipping');

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      {/* Hero */}
      <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-b border-rose-100 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-rose-100 text-rose-600 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
            <Shield size={14} /> Policies
          </span>
          <h1 className="font-display font-bold text-4xl text-gray-900 mb-3">
            Shipping & Return Policy
          </h1>
          <p className="text-gray-400 font-body text-lg">
            We want your gifting experience to be perfect. Here's everything you need to know. 💕
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Tabs */}
        <div className="flex bg-white rounded-2xl p-1 shadow-soft mb-8 w-fit">
          {[
            { id: 'shipping', label: '🚚 Shipping' },
            { id: 'returns', label: '🔄 Returns' },
            { id: 'faq', label: '❓ FAQs' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2.5 rounded-xl text-sm font-body font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-rose-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* SHIPPING TAB */}
        {activeTab === 'shipping' && (
          <div className="space-y-6 animate-fade-in">

            {/* Delivery Times */}
            <Section icon={Truck} title="Delivery Information" color="border-rose-500">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { zone: 'Metro Cities', time: '3–5 Business Days', cities: 'Mumbai, Delhi, Bangalore, Chennai, Hyderabad, Pune', color: 'bg-green-50 border-green-200 text-green-700' },
                  { zone: 'Tier 2 Cities', time: '5–7 Business Days', cities: 'Jaipur, Lucknow, Indore, Nagpur, Surat, Coimbatore', color: 'bg-blue-50 border-blue-200 text-blue-700' },
                  { zone: 'Tier 3 & Rural', time: '7–10 Business Days', cities: 'Remote areas, villages, and northeastern states', color: 'bg-amber-50 border-amber-200 text-amber-700' },
                  { zone: 'Handmade Items', time: '2–3 Days Extra', cities: 'Handmade products need extra time for careful preparation', color: 'bg-rose-50 border-rose-200 text-rose-700' },
                ].map((item) => (
                  <div key={item.zone} className={`border rounded-2xl p-4 ${item.color.split(' ')[0]} ${item.color.split(' ')[1]}`}>
                    <p className={`font-body font-bold text-sm ${item.color.split(' ')[2]}`}>{item.zone}</p>
                    <p className="font-display font-bold text-lg text-gray-900 my-1">{item.time}</p>
                    <p className="text-gray-500 font-body text-xs">{item.cities}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Shipping Charges */}
            <Section icon={Clock} title="Shipping Charges" color="border-green-500">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🎉</span>
                    <div>
                      <p className="font-body font-bold text-gray-900">All Orders</p>
                      <p className="text-gray-500 font-body text-xs">No minimum order value required</p>
                    </div>
                  </div>
                  <span className="bg-green-500 text-white font-bold px-4 py-2 rounded-full text-sm">FREE</span>
                </div>
                <p className="text-gray-400 font-body text-xs text-center">
                  🚚 We offer FREE delivery on all orders across India — no conditions!
                </p>
              </div>
            </Section>

            {/* Order Processing */}
            <Section icon={Clock} title="Order Processing" color="border-blue-500">
              <div className="space-y-3">
                {[
                  { step: '1', title: 'Order Confirmed', desc: 'You receive a confirmation email immediately after payment', time: 'Instant' },
                  { step: '2', title: 'Preparing Your Gift', desc: 'We carefully pack your order with love and gift wrapping', time: '24–48 hours' },
                  { step: '3', title: 'Shipped', desc: 'Your order is handed to our delivery partner with tracking', time: 'Day 2–3' },
                  { step: '4', title: 'Delivered', desc: 'Your gift arrives at the destination with care', time: 'As per zone' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center text-rose-600 font-bold text-sm shrink-0 mt-0.5">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-body font-semibold text-gray-900 text-sm">{item.title}</p>
                        <span className="text-xs text-rose-500 font-body font-medium bg-rose-50 px-2 py-0.5 rounded-full">{item.time}</span>
                      </div>
                      <p className="text-gray-400 font-body text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Shipping Notes */}
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5">
              <h3 className="font-body font-semibold text-rose-700 mb-3 text-sm">📌 Important Notes</h3>
              <ul className="space-y-2">
                {[
                  'Orders placed before 2 PM IST are processed the same day',
                  'We ship Monday to Saturday (excluding public holidays)',
                  'Tracking details will be sent via email once shipped',
                  'For urgent/same-day delivery, please WhatsApp us directly',
                  'We currently ship across all states in India',
                ].map((note, i) => (
                  <li key={i} className="flex items-start gap-2 text-rose-600 font-body text-xs">
                    <span className="mt-0.5 shrink-0">•</span> {note}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* RETURNS TAB */}
        {activeTab === 'returns' && (
          <div className="space-y-6 animate-fade-in">

            <Section icon={RefreshCw} title="Return Policy" color="border-purple-500">
              <div className="grid sm:grid-cols-3 gap-4 mb-5">
                {[
                  { emoji: '📦', title: 'Damaged Item', eligible: true },
                  { emoji: '❌', title: 'Wrong Item', eligible: true },
                  { emoji: '🎨', title: 'Personalized', eligible: false },
                ].map((item) => (
                  <div key={item.title} className={`rounded-2xl p-4 text-center border ${item.eligible ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <span className="text-3xl">{item.emoji}</span>
                    <p className="font-body font-medium text-gray-800 text-sm mt-2">{item.title}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full mt-1 inline-block ${item.eligible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {item.eligible ? 'Eligible' : 'Not Eligible'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <h3 className="font-body font-semibold text-gray-800 text-sm">Return Conditions:</h3>
                {[
                  { ok: true, text: 'Item must be returned within 7 days of delivery' },
                  { ok: true, text: 'Item must be in original condition and packaging' },
                  { ok: true, text: 'Photo/video proof of damage required' },
                  { ok: false, text: 'Personalized or custom items cannot be returned' },
                  { ok: false, text: 'Items damaged due to misuse are not eligible' },
                  { ok: false, text: 'Returns without prior approval will not be accepted' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className={`text-sm shrink-0 mt-0.5 ${item.ok ? 'text-green-500' : 'text-red-500'}`}>
                      {item.ok ? '✓' : '✗'}
                    </span>
                    <p className="font-body text-sm text-gray-600">{item.text}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Refund Process */}
            <Section icon={Clock} title="Refund Process" color="border-amber-500">
              <div className="space-y-3">
                {[
                  { step: '1', title: 'Contact Us', desc: 'WhatsApp or email us with your order ID and photos of the issue', time: 'Day 1' },
                  { step: '2', title: 'Review', desc: 'Our team reviews your request within 24 hours', time: '24 hours' },
                  { step: '3', title: 'Approval', desc: 'If approved, we send return pickup or replacement instructions', time: 'Day 2–3' },
                  { step: '4', title: 'Refund / Replacement', desc: 'Refund to original payment method or replacement shipped', time: '5–7 days' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-bold text-sm shrink-0 mt-0.5">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-body font-semibold text-gray-900 text-sm">{item.title}</p>
                        <span className="text-xs text-amber-600 font-body font-medium bg-amber-50 px-2 py-0.5 rounded-full">{item.time}</span>
                      </div>
                      <p className="text-gray-400 font-body text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Contact for Returns */}
            <div className="bg-gray-900 rounded-3xl p-6 text-white">
              <h3 className="font-display font-bold text-lg mb-4">To Initiate a Return</h3>
              <div className="space-y-3">
                <a
                  href="https://wa.me/919876543210"
                  className="flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-2xl transition-colors font-body font-medium text-sm"
                >
                  <span className="text-lg">💬</span> WhatsApp Us — +91 98765 43210
                </a>
                <a
                  href="mailto:hello@couplecart.in"
                  className="flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-2xl transition-colors font-body font-medium text-sm"
                >
                  <Mail size={18} /> hello@couplecart.in
                </a>
              </div>
              <p className="text-gray-400 font-body text-xs mt-4">
                Please include your Order ID and photos/video of the issue for faster resolution.
              </p>
            </div>
          </div>
        )}

        {/* FAQ TAB */}
        {activeTab === 'faq' && (
          <div className="space-y-3 animate-fade-in">
            {[
              { q: 'How long does delivery take?', a: 'Metro cities: 3–5 days. Tier 2 cities: 5–7 days. Remote areas: 7–10 days. Handmade items may take 2–3 extra days for preparation.' },
              { q: 'Is delivery really free?', a: 'Yes! We offer FREE delivery on all orders across India. No minimum order value required.' },
              { q: 'Can I cancel my order?', a: 'You can cancel within 2 hours of placing the order by contacting us on WhatsApp. Once the order is packed and shipped, it cannot be cancelled.' },
              { q: 'What if I receive a damaged product?', a: 'Please take photos/video immediately and contact us on WhatsApp within 48 hours of delivery. We will arrange a replacement or full refund.' },
              { q: 'Can I return a personalized gift?', a: 'Unfortunately, personalized and custom-made items cannot be returned unless they arrive damaged or incorrect.' },
              { q: 'How will I know my order is shipped?', a: 'You will receive an email with tracking details once your order is dispatched. You can also check your order status on our Orders page.' },
              { q: 'Do you ship outside India?', a: 'Currently we only ship within India. We are working on international shipping and will announce it soon!' },
              { q: 'Can I change my delivery address?', a: 'Address changes can be made within 2 hours of placing the order. Contact us immediately on WhatsApp with your order ID.' },
              { q: 'How long does a refund take?', a: 'Once approved, refunds are processed within 5–7 business days to your original payment method (UPI, card, net banking).' },
              { q: 'Do you offer gift wrapping?', a: 'Yes! All orders come with complimentary gift wrapping. You can also add a personalized note at checkout.' },
            ].map((item, i) => (
              <FAQ key={i} q={item.q} a={item.a} />
            ))}

            {/* Still have questions */}
            <div className="bg-rose-50 border border-rose-200 rounded-3xl p-6 text-center mt-6">
              <p className="text-2xl mb-2">💬</p>
              <h3 className="font-display font-bold text-gray-900 mb-1">Still have questions?</h3>
              <p className="text-gray-400 font-body text-sm mb-4">We're here to help! Reach out anytime.</p>
              <a
                href="https://wa.me/919876543210"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium text-sm transition-colors"
              >
                💬 Chat on WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
